import React, {useEffect, useState} from "react";
import {Link} from "react-router-dom";

/**
 * Cookie consent banner shown only on the main EAS landing page (/). The webinar funnel
 * skips it — visitors there come from paid ads with prior context, and the funnel needs
 * tracking to fire as soon as they land.
 *
 * Stores `eas-cookie-consent: accepted | declined` in localStorage. Tracking scripts
 * (Meta Pixel / GA / Clarity / TikTok Pixel) load lazily on user interaction/idle anyway,
 * but with `declined` we set a global flag that the lazy loaders read before firing.
 */
const STORAGE_KEY = "eas-cookie-consent";

type ConsentValue = "accepted" | "declined" | null;

function readStoredConsent(): ConsentValue {
    try {
        const v = localStorage.getItem(STORAGE_KEY);
        return v === "accepted" || v === "declined" ? v : null;
    } catch {
        return null;
    }
}

function writeConsent(value: "accepted" | "declined"): void {
    try {
        localStorage.setItem(STORAGE_KEY, value);
    } catch {
        // best-effort — Safari private mode disallows writes
    }
    // Mirror the choice on a global flag so the lazy script loaders can short-circuit
    // before pushing to dataLayer / fbq when the user has declined non-essential cookies.
    (window as any).__easCookieConsent = value;
}

export default function CookieBanner(): JSX.Element | null {
    const [consent, setConsent] = useState<ConsentValue>(null);

    useEffect(() => {
        const stored = readStoredConsent();
        setConsent(stored);
        if (stored) {
            (window as any).__easCookieConsent = stored;
        }
    }, []);

    if (consent !== null) {
        return null;
    }

    function accept() {
        writeConsent("accepted");
        setConsent("accepted");
    }

    function decline() {
        writeConsent("declined");
        setConsent("declined");
    }

    return (
        <div
            role="dialog"
            aria-live="polite"
            aria-label="Cookie consent"
            style={{
                position: "fixed",
                bottom: 16,
                left: 16,
                right: 16,
                zIndex: 9999,
                maxWidth: 640,
                margin: "0 auto",
                padding: "1rem 1.25rem",
                background: "rgba(11, 20, 16, 0.96)",
                backdropFilter: "blur(8px)",
                color: "#fff",
                border: "1px solid rgba(42, 209, 103, 0.3)",
                borderRadius: 14,
                boxShadow: "0 16px 48px rgba(0,0,0,0.4)",
                fontSize: "0.95rem",
                lineHeight: 1.5,
            }}
        >
            <p style={{margin: "0 0 0.75rem"}}>
                We use cookies to make the site work, measure traffic, and improve our marketing.{" "}
                <Link to="/legal/privacy" style={{color: "#2ad167", textDecoration: "underline"}}>
                    Learn more
                </Link>
                .
            </p>
            <div style={{display: "flex", gap: 8, flexWrap: "wrap"}}>
                <button
                    type="button"
                    onClick={accept}
                    style={{
                        flex: "1 1 140px",
                        padding: "0.6rem 1rem",
                        background: "#2ad167",
                        color: "#0b1410",
                        border: "none",
                        borderRadius: 10,
                        fontWeight: 700,
                        cursor: "pointer",
                    }}
                >
                    Accept all
                </button>
                <button
                    type="button"
                    onClick={decline}
                    style={{
                        flex: "1 1 140px",
                        padding: "0.6rem 1rem",
                        background: "transparent",
                        color: "rgba(255,255,255,0.85)",
                        border: "1px solid rgba(255,255,255,0.25)",
                        borderRadius: 10,
                        fontWeight: 600,
                        cursor: "pointer",
                    }}
                >
                    Decline non-essential
                </button>
            </div>
        </div>
    );
}
